import React, { useEffect, useRef } from 'react';
import { IconButton } from './Button';

export function Modal({ 
  open, 
  onClose, 
  children, 
  maxWidth = 'xl', // 'xs', 'sm', 'md', 'lg', 'xl', '2xl'
  fullWidth = false,
  fullScreen = false,
  className = '',
  overlayClassName = '',
  ...props 
}) {
  const modalRef = useRef(null);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && open) {
        onClose?.();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [open, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose?.();
    }
  };

  if (!open) return null;

  const maxWidthClasses = {
    xs: 'max-w-xs',
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-4xl',
    '2xl': 'max-w-6xl'
  };

  const modalClasses = `
    fixed inset-0 z-50 flex items-center justify-center p-4
    bg-black bg-opacity-70 backdrop-blur-sm
    ${overlayClassName}
  `;

  const contentClasses = `
    relative bg-white bg-opacity-95 backdrop-blur-md rounded-xl shadow-2xl
    max-h-[95vh] overflow-hidden flex flex-col
    ${fullScreen 
      ? 'w-full h-full max-w-none max-h-none rounded-none' 
      : `${maxWidthClasses[maxWidth]} ${fullWidth ? 'w-full' : ''}`
    }
    ${className}
  `;

  return (
    <div className={modalClasses} onClick={handleBackdropClick}>
      <div className={contentClasses} ref={modalRef} {...props}>
        {children}
      </div>
    </div>
  );
}

export function ModalHeader({ children, onClose, className = '', ...props }) {
  const headerClasses = `
    flex items-start justify-between p-6 border-b border-gray-200 border-opacity-20
    ${className}
  `;

  return (
    <div className={headerClasses} {...props}>
      <div className="flex-1 pr-4">
        {children}
      </div>
      {onClose && (
        <IconButton onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </IconButton>
      )}
    </div>
  );
}

export function ModalContent({ children, className = '', ...props }) {
  const contentClasses = `
    flex-1 overflow-y-auto text-gray-800
    ${className}
  `;

  return (
    <div className={contentClasses} {...props}>
      {children}
    </div>
  );
}

export function ModalActions({ children, className = '', ...props }) {
  const actionsClasses = `
    flex items-center justify-end gap-3 p-6 border-t border-gray-200 border-opacity-20
    ${className}
  `;

  return (
    <div className={actionsClasses} {...props}>
      {children}
    </div>
  );
} 